import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState } from "react";
import { Form, Input, Select, DatePicker, Row, Col, InputNumber, Switch, } from "antd";
const { RangePicker } = DatePicker;
const { Option } = Select;
/**
 * 通用搜索组件（内核）
 * 只包含搜索表单核心功能，不包含容器
 */
const CompsSearch = ({ searchItems = [], columns = 3, onSearch, onReset, collapsible = true, defaultCollapsed = false, collapsed: externalCollapsed, onCollapsedChange, labelWidth = 100, className, style, showButtons = true, buttonPosition = "right", customButtons, formRef, onButtonsRender, onValuesChange, }) => {
    const [form] = Form.useForm();
    const [internalCollapsed, setInternalCollapsed] = useState(defaultCollapsed);
    // 使用外部控制的 collapsed 或内部状态
    const collapsed = externalCollapsed !== undefined ? externalCollapsed : internalCollapsed;
    // 切换折叠状态（保留用于向后兼容，当 showButtons=true 时可能使用）
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const toggleCollapse = () => {
        const newCollapsed = !collapsed;
        if (onCollapsedChange) {
            onCollapsedChange(newCollapsed);
        }
        else {
            setInternalCollapsed(newCollapsed);
        }
    };
    // 将表单实例传递给父组件
    React.useEffect(() => {
        if (formRef && form) {
            formRef(form);
        }
    }, [form, formRef]);
    // 处理搜索（保留用于向后兼容，当 showButtons=true 时可能使用）
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const handleSearch = () => {
        console.log("[CompsSearch] 搜索按钮被点击");
        form
            .validateFields()
            .then((values) => {
            console.log("[CompsSearch] 表单验证成功，搜索值:", values);
            onSearch(values);
        })
            .catch((errorInfo) => {
            console.log("[CompsSearch] 表单验证失败:", errorInfo);
            // 即使验证失败，也执行搜索（使用当前表单值）
            const currentValues = form.getFieldsValue();
            console.log("[CompsSearch] 使用当前表单值进行搜索:", currentValues);
            onSearch(currentValues);
        });
    };
    // 重置表单（保留用于向后兼容，当 showButtons=true 时可能使用）
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const handleReset = () => {
        console.log("[CompsSearch] 重置按钮被点击");
        form.resetFields();
        console.log("[CompsSearch] 表单已重置");
        // 如果有onReset回调，优先调用onReset，否则执行空搜索
        if (onReset) {
            console.log("[CompsSearch] 调用onReset回调");
            onReset();
        }
        else {
            console.log("[CompsSearch] 执行空搜索");
            onSearch({});
        }
    };
    // 计算每个表单项占用的列数
    const getColSpan = (item) => {
        if (item.colSpan)
            return item.colSpan;
        return 24 / columns;
    };
    // 根据折叠状态确定显示的项目
    const visibleItems = collapsed && collapsible ? searchItems.slice(0, columns) : searchItems;
    // 渲染搜索控件（默认 width:100% 充满列宽）
    const renderFormItem = (item) => {
        const { type, options, props = {}, customComponent } = item;
        const style = { width: "100%", ...props.style };
        switch (type) {
            case "input":
                return _jsx(Input, { allowClear: true, style: style, ...props });
            case "select":
                return (_jsx(Select, { allowClear: true, style: style, ...props, children: options?.map((opt) => (_jsx(Option, { value: opt.value, label: opt.label, children: opt.label }, String(opt.value)))) }));
            case "date":
                return _jsx(DatePicker, { style: style, ...props });
            case "dateRange":
                // RangePicker 支持 picker 属性来切换选择器类型
                // picker="date" - 日期选择器
                // picker="month" - 月份选择器
                // picker="year" - 年份选择器
                return _jsx(RangePicker, { style: style, ...props });
            case "number":
                return _jsx(Input, { type: "number", allowClear: true, style: style, ...props });
            case "inputNumber":
                return _jsx(InputNumber, { style: style, ...props });
            case "switch":
                return _jsx(Switch, { ...props });
            case "custom":
                if (customComponent) {
                    const CustomComponent = customComponent;
                    return _jsx(CustomComponent, { style: style, ...props });
                }
                return _jsx(Input, { allowClear: true, style: style, ...props });
            default:
                return _jsx(Input, { allowClear: true, style: style, ...props });
        }
    };
    // 计算标签宽度的样式
    const labelColStyle = {
        width: typeof labelWidth === "number" ? `${labelWidth}px` : labelWidth,
        textAlign: "right",
    };
    // 构建容器样式
    const containerStyle = {
        ...style,
    };
    return (_jsxs("div", { className: className, style: containerStyle, children: [_jsx(Form, { form: form, layout: "horizontal", onValuesChange: onValuesChange, children: _jsx(Row, { gutter: [16, 16], children: visibleItems.map((item) => {
                        // 如果字段有 dependencies，需要使用 Form.Item 的 shouldUpdate 来动态更新
                        if (item.dependencies && item.dependencies.length > 0) {
                            return (_jsx(Col, { span: getColSpan(item), children: _jsx(Form.Item, { noStyle: true, shouldUpdate: (prevValues, currentValues) => {
                                        // 检查依赖字段是否有变化
                                        return (item.dependencies?.some((dep) => prevValues[dep] !== currentValues[dep]) || false);
                                    }, children: ({ getFieldValue, getFieldsValue }) => {
                                        // 根据依赖字段的值动态设置 disabled
                                        let disabled = false;
                                        if (typeof item.disabled === "function") {
                                            // 使用 getFieldsValue 获取所有表单值，确保获取到最新值
                                            const formValues = getFieldsValue();
                                            disabled = item.disabled(formValues);
                                            // 调试日志（仅在开发环境）
                                            if (process.env.NODE_ENV === "development" &&
                                                item.name === "dateRange") {
                                                console.log(`[CompsSearch] ${item.name} disabled状态:`, disabled, "dateType:", formValues.dateType);
                                            }
                                        }
                                        else if (typeof item.disabled === "boolean") {
                                            disabled = item.disabled;
                                        }
                                        // 收集依赖字段的值
                                        const dependencyValues = {};
                                        if (item.dependencies && item.dependencies.length > 0) {
                                            item.dependencies.forEach((dep) => {
                                                dependencyValues[dep] = getFieldValue(dep);
                                            });
                                        }
                                        // 根据 dateType 动态设置 picker 模式（仅对 dateRange 类型）
                                        let finalProps = { ...item.props, disabled };
                                        if (item.type === "dateRange" &&
                                            dependencyValues.dateType !== undefined) {
                                            const dateType = dependencyValues.dateType;
                                            // 根据统计维度设置不同的 picker 模式
                                            if (dateType === 0) {
                                                // 按日统计：使用日期选择器
                                                finalProps.picker = "date";
                                                finalProps.format = "YYYY-MM-DD";
                                            }
                                            else if (dateType === 1) {
                                                // 按月统计：使用月份选择器
                                                finalProps.picker = "month";
                                                finalProps.format = "YYYY-MM";
                                            }
                                            else if (dateType === 2) {
                                                // 按年统计：使用年份选择器
                                                finalProps.picker = "year";
                                                finalProps.format = "YYYY";
                                            }
                                        }
                                        const itemWithDisabled = {
                                            ...item,
                                            props: {
                                                ...finalProps,
                                                // 将依赖字段的值作为props传递给custom组件
                                                ...(item.type === "custom" ? dependencyValues : {}),
                                            },
                                        };
                                        return (_jsx(Form.Item, { name: item.name, label: _jsx("span", { style: { color: "#1f1f1f" }, children: item.label }), style: { marginBottom: 0 }, labelCol: { style: labelColStyle }, dependencies: item.dependencies, valuePropName: item.type === "switch" ? "checked" : "value", children: renderFormItem(itemWithDisabled) }));
                                    } }) }, item.name));
                        }
                        // 处理普通表单项
                        let disabled = false;
                        if (typeof item.disabled === "function") {
                            const formValues = form.getFieldsValue();
                            disabled = item.disabled(formValues);
                        }
                        else if (typeof item.disabled === "boolean") {
                            disabled = item.disabled;
                        }
                        // 对于 dateRange 类型，如果配置了 dependencies，需要从表单获取 dateType
                        let finalProps = { ...item.props, disabled };
                        if (item.type === "dateRange" &&
                            item.dependencies?.includes("dateType")) {
                            const formValues = form.getFieldsValue();
                            const dateType = formValues.dateType;
                            if (dateType !== undefined) {
                                // 根据统计维度设置不同的 picker 模式
                                if (dateType === 0) {
                                    // 按日统计：使用日期选择器
                                    finalProps.picker = "date";
                                    finalProps.format = "YYYY-MM-DD";
                                }
                                else if (dateType === 1) {
                                    // 按月统计：使用月份选择器
                                    finalProps.picker = "month";
                                    finalProps.format = "YYYY-MM";
                                }
                                else if (dateType === 2) {
                                    // 按年统计：使用年份选择器
                                    finalProps.picker = "year";
                                    finalProps.format = "YYYY";
                                }
                            }
                        }
                        const itemWithDisabled = {
                            ...item,
                            props: finalProps,
                        };
                        return (_jsx(Col, { span: getColSpan(item), children: _jsx(Form.Item, { name: item.name, label: _jsx("span", { style: { color: "#1f1f1f" }, children: item.label }), style: { marginBottom: 0 }, labelCol: { style: labelColStyle }, dependencies: item.dependencies, valuePropName: item.type === "switch" ? "checked" : "value", children: renderFormItem(itemWithDisabled) }) }, item.name));
                    }) }) }), showButtons && buttonPosition !== "header" && customButtons && (_jsx("div", { style: {
                    display: "flex",
                    justifyContent: buttonPosition === "left" ? "flex-start" : "flex-end",
                    marginTop: 16,
                }, children: customButtons }))] }));
};
export default CompsSearch;
